// Adapter — Wave B2.
//
// Maps the API `ConversationDetail` envelope into the DS `ContextPanelData`
// shape so the inbox right-rail mounts with real data instead of `null`.
// Returns `null` when:
//   - The conversation envelope is mid-load (caller should keep the
//     placeholder or empty state visible).
//   - Critical fields are missing (no account id) — defensive, never
//     happens in practice but the wrapper renders `null` gracefully.

import type { ConversationDetail, LinkedinAccountRow } from '@barel/api-client/react';
import type { ContextPanelData } from '@barel/ui';

export interface ToContextPanelArgs {
  envelope: ConversationDetail | null | undefined;
  account: Pick<LinkedinAccountRow, 'id' | 'name' | 'color' | 'aiMode'> | null | undefined;
  /** Optional display override for the contact's stored role/company. */
  contact?: { title?: string | null; company?: string | null };
}

/** Best-effort mapper — returns `null` when inputs aren't ready. */
export function toContextPanelData({
  envelope,
  account,
  contact,
}: ToContextPanelArgs): ContextPanelData | null {
  if (!envelope) return null;
  const accountId = account?.id ?? envelope.accountId;
  if (!accountId) return null;

  const aiMode = envelope.aiMode ?? account?.aiMode ?? 'review';
  // The DS composite types `aiMode` strictly as `'auto' | 'review'` (manual
  // accounts get treated as review for the descriptive copy — they still
  // need a draft surface). We collapse the API tri-state down here.
  const dsAiMode: ContextPanelData['aiMode'] = aiMode === 'auto' ? 'auto' : 'review';

  return {
    id: envelope.id,
    name: envelope.contactName,
    title: contact?.title ?? envelope.contactRole ?? '',
    company: contact?.company ?? envelope.contactCompany ?? '',
    initials: envelope.contactInitials,
    accountColor: account?.color ?? envelope.accountColor ?? 'var(--accent-default)',
    accountId,
    accountLabel: account?.name ?? envelope.accountName,
    accountSubtitle: envelope.accountSubtitle ?? '',
    business: envelope.primaryBusinessName ?? '',
    businessDescription: envelope.businessDescription ?? '',
    campaign: envelope.campaignName ?? undefined,
    source: envelope.source,
    sourceAutoDetected: envelope.sourceDetected !== false,
    aiMode: dsAiMode,
  };
}
